/**
 * Where the anchor actually goes in.
 *
 * Every "drop anchor here" button used the ownship position, which is the GNSS
 * ANTENNA. The anchor leaves the bow roller. On a yacht with the aerial aft
 * that is most of a boat-length of error, and it lands in the one calculation
 * where it matters most: the swinging circle is centred on the anchor, so an
 * anchor recorded several metres aft of where it went in gives a circle
 * several metres out of place all night (fb-msbyab1j-0).
 *
 * THE OFFSET NEEDS A DIRECTION, and that is the part the first version got
 * wrong. It read ownship.heading straight, and on a phone with no compass that
 * sits at 0 forever, which is not "unknown", it is due north. So the bow was
 * assumed to be 11.5 m north of the aerial whatever the boat was doing, and an
 * anchor placed 15 m away on 110 came out looking like 066 (fb-msdngi8e-0).
 * The correction was bigger than the thing being corrected.
 *
 * So the offset is applied only when we genuinely know which way the bow
 * points: a real heading, or course over ground while we are moving. With
 * neither, we hand back the fix unmoved. An uncorrected anchor is out by the
 * distance from the aerial to the bow; one corrected in a made-up direction can
 * be out by twice that, in a direction nobody can predict. Not correcting is
 * the better failure.
 *
 * One function, reading the live stores, so the offset cannot end up applied
 * at three of the five places an anchor can be dropped.
 */
import { useChartPlotterStore } from '@/store';
import { useSettingsStore } from '@/store/settings';
import { usePositionSourceStore } from '@/store/positionSource';
import { ownshipIconHeading } from '@/map/ownshipIconHeading';
import { anchorDropPosition } from './hullOutline';

/**
 * Which way the bow points, or null when we genuinely do not know. Same rule as
 * the boat symbol uses, so the hull we draw and the anchor we drop can never
 * disagree about which way the boat is facing.
 */
export function bowHeadingNow(): number | null {
  const os = useChartPlotterStore.getState().ownship;
  const hasHeadingSource = usePositionSourceStore.getState().sourceKind === 'sim';
  return ownshipIconHeading({
    heading: hasHeadingSource ? os.heading : null,
    cog: os.cog,
    sog: os.sog,
    last: null,
  });
}

/** The bow position now, or the given fallback position when we have no fix. */
export function anchorPositionNow(fallback?: [number, number] | null): [number, number] | null {
  const os = useChartPlotterStore.getState().ownship;
  const position = os.position ?? fallback ?? null;
  if (!position) return null;
  const headingDeg = bowHeadingNow();
  // No heading, no offset: see the header. Better out by the aerial-to-bow
  // distance than out by twice it in a direction we invented.
  if (headingDeg == null) return position;
  const s = useSettingsStore.getState();
  return anchorDropPosition({
    position,
    headingDeg,
    gnssFromBowM: s.gnssFromBowM,
    gnssFromCentreM: s.gnssFromCentreM,
  });
}
